import { generateColorScale, getContrastText, isHexColor, isNonProductionEnvironment, type TBrandGradient } from '@glomopay/utils';

import { generateBrandRamp, generateDarkBrandRamp } from './ramp-generator';
import type { TTenantBrandingConfig } from './types';

export type TApplyTenantThemeInput = Pick<TTenantBrandingConfig, 'primaryColor' | 'fontFamily' | 'brandGradient'>;

const STYLE_ID = 'glomo-tenant-theme';

/** Strip characters that could break out of a CSS declaration context. */
const sanitizeCssValue = (value: string): string => value.replace(/[{}<>;]/g, '');

/**
 * Re-validates the stops even though the schema already did: this value is
 * interpolated into a <style> element, so a caller that skipped validation must
 * not be able to inject declarations. Returns null when the gradient is unusable,
 * which leaves --brand-fill at its flat default.
 */
const buildBrandFill = (gradient: TBrandGradient): string | null => {
  const { from, to, angle } = gradient;
  if (!isHexColor(from) || !isHexColor(to)) return null;
  if (!Number.isFinite(angle) || angle < 0 || angle > 360) return null;

  return `linear-gradient(${angle}deg, ${from}, ${to})`;
};

/**
 * Overrides the brand-resolving semantic aliases on :root from the tenant seed,
 * plus --text-on-brand (and shadcn's --primary-foreground) for contrast on the brand fill.
 */
export const applyTenantTheme = (branding: TApplyTenantThemeInput): void => {
  const seed = branding.primaryColor.trim();
  if (!seed) {
    // The gradient rides on the same seed that produces the ramp, text and border
    // colours, so there is nothing sane to apply without it. Surface the dropped
    // gradient outside production — the combination can only be a config mistake.
    if (branding.brandGradient && isNonProductionEnvironment()) {
      console.error(
        '[tenant-theme] Ignoring brandGradient because primaryColor is empty. A tenant that sets a gradient must also set a primaryColor.',
      );
    }
    return;
  }
  // Branding isn't validated on write, and the ramp generator throws on a non-colour seed, which would take
  // down whichever screen applied it. Keeping the defaults is the safe outcome.
  if (!isHexColor(seed)) return;

  const brand = generateBrandRamp(seed);
  const declarations: string[] = [
    `--brand-primary: ${brand.primary} !important`,
    `--bg-brand-solid: ${brand.primary} !important`,
    `--text-brand: ${brand.primary} !important`,
    `--border-brand: ${brand.primary} !important`,
    `--border-focus: ${brand.primary} !important`,
    `--icon-brand: ${brand.primary} !important`,
    `--interactive-default: ${brand.primary} !important`,
    `--interactive-hover: ${brand.hover} !important`,
    `--brand-primary-hover: ${brand.hover} !important`,
    `--brand-primary-subtle: ${brand.subtle} !important`,
    `--brand-primary-muted: ${brand.muted} !important`,
    `--bg-brand: ${brand.subtle} !important`,
    `--interactive-active: ${brand.hover} !important`,
  ];

  if (branding.brandGradient) {
    const brandFill = buildBrandFill(branding.brandGradient);
    if (brandFill) {
      declarations.push(`--brand-fill: ${brandFill} !important`);
      declarations.push(`--brand-fill-from: ${branding.brandGradient.from} !important`);
    }
  }

  const scale = generateColorScale(seed);
  const foreground = getContrastText(scale[500]);
  declarations.push(`--primary-foreground: ${foreground} !important`);
  declarations.push(`--text-on-brand: ${foreground} !important`);

  if (branding.fontFamily) {
    declarations.push(`--tenant-font-family: ${sanitizeCssValue(branding.fontFamily)} !important`);
  }

  const dark = generateDarkBrandRamp(seed);
  const darkForeground = getContrastText(dark.primary);
  const darkDeclarations: string[] = [
    `--brand-primary: ${dark.primary} !important`,
    `--bg-brand-solid: ${dark.primary} !important`,
    `--text-brand: ${dark.text} !important`,
    `--border-brand: ${dark.text} !important`,
    `--border-focus: ${dark.text} !important`,
    `--icon-brand: ${dark.text} !important`,
    `--interactive-default: ${dark.text} !important`,
    `--interactive-hover: ${dark.textHover} !important`,
    `--interactive-active: ${dark.text} !important`,
    `--brand-primary-hover: ${dark.hover} !important`,
    `--brand-primary-subtle: color-mix(in srgb, ${dark.primary} 20%, var(--bg-default)) !important`,
    `--brand-primary-muted: color-mix(in srgb, ${dark.primary} 32%, var(--bg-default)) !important`,
    `--bg-brand: color-mix(in srgb, ${dark.primary} 20%, var(--bg-default)) !important`,
    `--primary-foreground: ${darkForeground} !important`,
    `--text-on-brand: ${darkForeground} !important`,
  ];
  if (branding.fontFamily) {
    darkDeclarations.push(`--tenant-font-family: ${sanitizeCssValue(branding.fontFamily)} !important`);
  }

  // The light ramp is pale-on-white; on dark surfaces the seed's own hue is re-derived at the dark
  // stops instead. A light island (.light) inside a dark page keeps the light ramp. A gradient fill
  // stays light-only: dark falls back to the flat --brand-fill of the dark primary.
  const block = declarations.join('; ');
  const darkBlock = darkDeclarations.join('; ');
  const css = `:root:not(.dark), .light { ${block}; } html:not(.dark) { ${block}; } .dark { ${darkBlock}; }`;

  let styleEl = document.getElementById(STYLE_ID) as HTMLStyleElement | null;
  if (!styleEl) {
    styleEl = document.createElement('style');
    styleEl.id = STYLE_ID;
    document.head.appendChild(styleEl);
  }
  styleEl.textContent = css;
};

export const removeTenantTheme = (): void => {
  document.getElementById(STYLE_ID)?.remove();
};
